import { ViewBooking, ViewBookingStatusEnum } from "collins-js-sdk";
import dayjs, { Dayjs } from "dayjs";
import { BookingType, VenueConfig } from "../types/venues";
import { humanizeDuration } from "./helpers";

type GetBookingType = {
  venueConfig?: VenueConfig | null;
  selectedBooking: ViewBooking;
};
export const getBookingType = ({
  venueConfig,
  selectedBooking
}: GetBookingType): BookingType | null | undefined => {
  return getBookingTypeById({
    venueConfig,
    bookingTypeId: selectedBooking.type?.id
  });
};

export const getBookingTypeById = ({
  venueConfig,
  bookingTypeId
}: {
  venueConfig?: VenueConfig | null;
  bookingTypeId?: string | null;
}): BookingType | null | undefined => {
  return venueConfig?.booking_types && bookingTypeId
    ? venueConfig.booking_types
        .map((bookingType) => ({
          id: bookingType.id,
          name: bookingType.name,
          color: bookingType.color
        }))
        .find((bookingType) => bookingType.id === bookingTypeId)
    : null;
};

export const getBookingDateTime = (selectedBooking: ViewBooking): Dayjs => {
  let time = selectedBooking.time;
  if (
    selectedBooking.waitlisted &&
    selectedBooking.waitlistDetails?.availableTime &&
    !selectedBooking.currentStage
  ) {
    time = getWaitlistTimeString(selectedBooking.waitlistDetails.availableTime);
  }

  let bookingTimeParts = time ? time.split(":") : ["0", "0"];
  if (bookingTimeParts.length < 2) {
    bookingTimeParts = ["0", "0"];
  }

  const date = new Date(selectedBooking.date ?? "");

  return dayjs(date)
    .hour(parseInt(bookingTimeParts[0]))
    .minute(parseInt(bookingTimeParts[1]));
};

export const getWaitlistTimeString = (availableTime: string): string => {
  let dateTimeString = availableTime;
  if (dateTimeString.indexOf("T") === -1) {
    dateTimeString = availableTime.replace(/-/g, "/");
  }

  return new Date(dateTimeString).toLocaleTimeString("en-GB", {
    hour: "numeric",
    minute: "numeric"
  });
};

export const getBookingTimeAsString = (
  selectedBooking: ViewBooking
): string => {
  if (
    selectedBooking.waitlisted &&
    !selectedBooking.waitlistDetails?.availableTime &&
    !selectedBooking.currentStage
  ) {
    return "ASAP";
  }

  return getBookingDateTime(selectedBooking).format("H:mm");
};

export const getBookingEndTimeAsString = (
  selectedBooking: ViewBooking
): string => {
  if (
    selectedBooking.waitlisted &&
    !selectedBooking.waitlistDetails?.availableTime &&
    !selectedBooking.currentStage
  ) {
    return "ASAP";
  }

  if (selectedBooking.duration === -1) {
    return "Close";
  }

  if (!selectedBooking.duration) {
    return "";
  }

  const bookingEndDate = getBookingDateTime(selectedBooking).add(
    selectedBooking.duration ? selectedBooking.duration : 0,
    "minute"
  );

  return bookingEndDate.format("H:mm");
};

export const getBookingDurationAsString = (
  selectedBooking: ViewBooking,
  style?: "short"
): string =>
  selectedBooking.duration
    ? humanizeDuration(selectedBooking.duration || 0, style)
    : style === "short"
      ? "-"
      : "No end time";

export const getBookingAssignedAreaNamesAsString = (
  selectedBooking: ViewBooking
): string => {
  let areasAsStringRecord =
    selectedBooking.assignedAreas && selectedBooking.assignedAreas.length > 0
      ? selectedBooking.assignedAreas.map((area) => area.name ?? "")
      : ["Unassigned"];

  if (
    selectedBooking.waitlisted &&
    selectedBooking.waitlistDetails?.area?.name
  ) {
    areasAsStringRecord = [selectedBooking.waitlistDetails?.area?.name];
  }

  const areasAsStringMultyRecords = areasAsStringRecord
    .slice(0, -1)
    .join(", ")
    .concat(" & ", areasAsStringRecord.slice(-1).toString());

  return (
    areasAsStringRecord.length > 1
      ? areasAsStringMultyRecords
      : areasAsStringRecord
  ).toString();
};

type GetBookingsZoneNamesAsStringType = {
  venueConfig?: VenueConfig | null;
  selectedBooking: ViewBooking;
};
export const getBookingsZoneNamesAsString = ({
  venueConfig,
  selectedBooking
}: GetBookingsZoneNamesAsStringType): string => {
  const zoneIds = selectedBooking.assignedAreas
    ? selectedBooking.assignedAreas
        .map((area) => area.zone)
        .filter((zone) => !!zone)
    : [];

  const zoneNames = zoneIds
    .map((zoneId) => {
      const zone = (venueConfig?.bookable_area_zones || []).find(
        (areaZone) => areaZone.id === zoneId?.toString()
      );

      return zone?.name ?? "";
    })
    .filter((zone) => !!zone);

  const zoneNamesAsStringRecord = zoneNames.filter(
    (val, id, array) => array.indexOf(val) === id
  );
  const zoneNamesAsStringMultyRecords = zoneNamesAsStringRecord
    .slice(0, -1)
    .join(", ")
    .concat(" & ", zoneNamesAsStringRecord.slice(-1).toString());
  return (
    zoneNamesAsStringRecord.length > 1
      ? zoneNamesAsStringMultyRecords
      : zoneNamesAsStringRecord
  ).toString();
};

export type BookingStatusType = {
  id: ViewBookingStatusEnum;
  colour: string;
  label: string;
  labelColour: string;
};
const bookingStatus: BookingStatusType[] = [
  {
    id: ViewBookingStatusEnum.New,
    colour: "#b00836",
    label: "New",
    labelColour: "black"
  },
  {
    id: ViewBookingStatusEnum.InProgress,
    colour: "#d5763f",
    label: "In Progress",
    labelColour: "black"
  },
  {
    id: ViewBookingStatusEnum.Complete,
    colour: "green",
    label: "Confirmed",
    labelColour: "black"
  },
  {
    id: ViewBookingStatusEnum.Rejected,
    colour: "#131315",
    label: "Cancelled",
    labelColour: "white"
  },
  {
    id: ViewBookingStatusEnum.Deleted,
    colour: "#131315",
    label: "Deleted",
    labelColour: "white"
  },
  {
    id: ViewBookingStatusEnum.Lost,
    colour: "#131315",
    label: "Lost",
    labelColour: "white"
  }
];

export const getBookingStatus = (
  selectedBooking: ViewBooking
): BookingStatusType => {
  return (
    bookingStatus.find((status) => status.id === selectedBooking.status) ??
    bookingStatus[0]
  );
};
